'use client';

import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Avatar from './avatar';
import { paths } from '../routes';
import { navItems } from '../data/navItems';

const Sidebar = ({ logout, user }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isExpanded, setIsExpanded] = useState(() => {
		const saved = localStorage.getItem('sidenavExpanded');
		return saved !== null ? JSON.parse(saved) : true;
	});

	const dropdownRef = useRef(null);
	const navigate = useNavigate();

	const toggleDropdown = () => setIsOpen(!isOpen);

	const toggleSidenav = () => {
		const newState = !isExpanded;
		setIsExpanded(newState);
		localStorage.setItem('sidenavExpanded', JSON.stringify(newState));
	};

	const menuItems = [
		{
			label: 'Institution Profile',
			action: () => {
				navigate(paths.PROFILE);
				setIsOpen(false);
			},
		},
		{
			label: 'Logout',
			action: () => {
				logout();
				setIsOpen(false);
			},
		},
	];

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Show loading state while user data is being fetched
	if (!user) {
		return (
			<div
				className={`${
					isExpanded ? 'w-64' : 'w-20'
				} relative h-screen bg-white p-4 border-r transition-all duration-300`}>
				<div className='flex items-center justify-center h-16'>
					<div className='animate-pulse'>
						<div className='w-10 h-10 bg-gray-200 rounded-full'></div>
					</div>
				</div>
			</div>
		);
	}

	const institutionName = user?.name || 'Institution';
	const contactEmail = user?.contactEmail || user?.email || 'Email';
	const logoSrc = user?.settings?.logo || user?.logo;

	return (
		<div
			className={`${
				isExpanded ? 'w-56' : 'w-20'
			} relative h-screen bg-white p-4 border-r transition-all duration-300`}>
			{/* Toggle Button */}
			<button
				onClick={toggleSidenav}
				className='absolute -right-3 top-5 bg-white rounded-full p-1 border shadow-md hover:bg-gray-50'>
				<svg
					className={`w-4 h-4 transform ${
						isExpanded ? 'rotate-0' : 'rotate-180'
					} transition-transform duration-300`}
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M15 19l-7-7 7-7'
					/>
				</svg>
			</button>

			{/* Header Section */}
			<div
				className={`items-center ${
					isExpanded ? 'gap-3' : 'justify-center'
				} cursor-pointer mb-12`}
				onClick={toggleDropdown}>
				<div className='flex'>
					<div className='flex w-[40px] h-[40px] flex-shrink-0'>
						<Avatar
							firstName={institutionName}
							imageSrc={logoSrc}
						/>
					</div>
				</div>

				{isExpanded && (
					<>
						<div className='flex-col space-y-1 w-5/6 overflow-clip mt-4'>
							<h1 className='font-[500] '>{institutionName}</h1>
						</div>
					</>
				)}
			</div>

			{/* Navigation */}
			{navItems.map((navItem, index) => (
				<div
					className='mb-4'
					key={index}>
					<NavItem
						label={navItem.title}
						to={navItem.to}
						activeIcon={navItem.activeIcon}
						inactiveIcon={navItem.inactiveIcon}
						isExpanded={isExpanded}
					/>
				</div>
			))}

			{/* Dropdown Menu */}
			{isOpen && (
				<div
					ref={dropdownRef}
					className={`bg-white w-[240px] fixed top-5 ${
						isExpanded ? 'left-[30px]' : 'left-[80px]'
					} z-50 border border-borderNeutral rounded-[8px] flex flex-col items-start justify-center gap-7 px-4 py-3 font-firma shadow-lg`}>
					<div className='flex flex-col'>
						<div className='w-[40px] h-[40px]'>
							<Avatar
								firstName={institutionName}
								imageSrc={logoSrc}
							/>
						</div>
						<h1 className='mt-4 font-[500] leading-[18px] -tracking-[3%]'>
							{institutionName}
						</h1>
						<p className='text-[14px] mt-1 font-[400] leading-[16px] -tracking-[3%] text-gray-500'>
							{contactEmail}
						</p>
					</div>
					<div className='flex flex-col items-center gap-4'>
						{menuItems.map((item, index) => (
							<button
								key={index}
								className='w-[205px] h-[50px] bg-bgNeutral rounded-[4px] p-3 leading-[18px] -tracking-[3%] text-left'
								onClick={item.action}>
								{item.label}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

const NavItem = ({ label, to, activeIcon, inactiveIcon, isExpanded }) => {
	return (
		<NavLink
			onClick={() => {
				localStorage.removeItem('activeTab');
				localStorage.removeItem('currentPage');
			}}
			to={to}
			className={({ isActive }) =>
				`flex items-center ${
					isExpanded
						? 'space-x-4 px-[16px]'
						: 'justify-center px-[8px]'
				} py-[14px] rounded-xl cursor-pointer transition-colors ${
					isActive ? 'bg-primary text-[#FFFFFF]' : 'bg-transparent'
				}`
			}
			title={!isExpanded ? label : undefined}>
			{({ isActive }) => (
				<div
					className={`flex items-center ${
						isExpanded ? 'space-x-2' : 'justify-center'
					}`}>
					{isActive ? activeIcon : inactiveIcon}
					{isExpanded && <span className='text-center'>{label}</span>}
				</div>
			)}
		</NavLink>
	);
};

export default Sidebar;
